import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { BackgroundSlider } from '../components/BackgroundSlider';
import { ArrowRight, ArrowDown, Lightbulb, Cog, Rocket, TrendingUp } from 'lucide-react';
interface BlueprintPageProps {
  onNavigate?: (path: string) => void;
}
export function BlueprintPage({
  onNavigate
}: BlueprintPageProps) {
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-654">
      <Navbar onNavigate={onNavigate} data-id="element-655" />

      {/* Background Slider */}
      <BackgroundSlider data-id="element-656" />

      {/* Hero */}
      <section className="py-16 text-center" data-id="element-657">
        <div className="mx-auto max-w-4xl px-4" data-id="element-658">
          <h1 className="text-4xl md:text-6xl font-bold mb-6" data-id="element-659">The Blueprint</h1>
          <p className="text-xl font-mono text-salon-red mb-4" data-id="element-660">
            Your transformation journey from chaos to authority
          </p>
          <p className="text-lg text-[#1f2933]/80" data-id="element-661">
            This is not a linear course. This is a complete transformation of
            how you create, distribute, and leverage content for authority.
          </p>
        </div>
      </section>

      {/* Journey Visualization */}
      <section className="py-16" data-id="element-662">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" data-id="element-663">
          {/* Stage 1: Entry */}
          <div className="mb-16" data-id="element-664">
            <div className="flex items-center gap-4 mb-8" data-id="element-665">
              <div className="w-12 h-12 rounded-full bg-salon-red flex items-center justify-center text-white font-bold" data-id="element-666">
                01
              </div>
              <div data-id="element-667">
                <h2 className="text-2xl font-bold" data-id="element-668">Entry: Where You Are Now</h2>
                <p className="font-mono text-sm text-[#9ca3af]" data-id="element-669">
                  The starting point
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 ml-16" data-id="element-670">
              <div className="border-2 border-dashed border-[#9ca3af] p-6 bg-white" data-id="element-671">
                <h3 className="font-bold mb-3" data-id="element-672">Inconsistent Output</h3>
                <p className="text-sm text-[#1f2933]/80" data-id="element-673">
                  You post when inspired, but there's no system. Weeks go by
                  without content.
                </p>
              </div>
              <div className="border-2 border-dashed border-[#9ca3af] p-6 bg-white" data-id="element-674">
                <h3 className="font-bold mb-3" data-id="element-675">No Clear Framework</h3>
                <p className="text-sm text-[#1f2933]/80" data-id="element-676">
                  Every piece feels like starting from scratch. No repeatable
                  process.
                </p>
              </div>
              <div className="border-2 border-dashed border-[#9ca3af] p-6 bg-white" data-id="element-677">
                <h3 className="font-bold mb-3" data-id="element-678">Limited Leverage</h3>
                <p className="text-sm text-[#1f2933]/80" data-id="element-679">
                  You're doing everything manually. Time-intensive with minimal
                  ROI.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center mb-16" data-id="element-680">
            <ArrowDown className="w-8 h-8 text-salon-red" data-id="element-681" />
          </div>

          {/* Stage 2: Framework Development */}
          <div className="mb-16" data-id="element-682">
            <div className="flex items-center gap-4 mb-8" data-id="element-683">
              <div className="w-12 h-12 rounded-full bg-salon-teal flex items-center justify-center text-white font-bold" data-id="element-684">
                02
              </div>
              <div data-id="element-685">
                <h2 className="text-2xl font-bold" data-id="element-686">
                  Framework: Build Your Foundation
                </h2>
                <p className="font-mono text-sm text-[#9ca3af]" data-id="element-687">Weeks 1-3</p>
              </div>
            </div>

            <div className="ml-16 bg-white border-2 border-salon-teal p-8" data-id="element-688">
              <div className="flex items-start gap-6 mb-6" data-id="element-689">
                <Lightbulb className="w-12 h-12 text-salon-teal flex-shrink-0" data-id="element-690" />
                <div data-id="element-691">
                  <h3 className="text-xl font-bold mb-3" data-id="element-692">
                    Create Your Custom Content Framework
                  </h3>
                  <p className="text-[#1f2933]/80 mb-4" data-id="element-693">
                    This is where transformation begins. We help you design a
                    content framework tailored to your expertise, audience, and
                    business goals.
                  </p>
                  <ul className="space-y-2" data-id="element-694">
                    <li className="flex items-start gap-2" data-id="element-695">
                      <span className="text-salon-teal font-bold" data-id="element-696">→</span>
                      <span className="text-sm" data-id="element-697">
                        Define your authority positioning and unique voice
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-698">
                      <span className="text-salon-teal font-bold" data-id="element-699">→</span>
                      <span className="text-sm" data-id="element-700">
                        Map your content pillars and themes
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-701">
                      <span className="text-salon-teal font-bold" data-id="element-702">→</span>
                      <span className="text-sm" data-id="element-703">
                        Design your content types and formats
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-704">
                      <span className="text-salon-teal font-bold" data-id="element-705">→</span>
                      <span className="text-sm" data-id="element-706">
                        Create your editorial calendar structure
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="bg-salon-teal/10 border-l-4 border-salon-teal p-4 font-mono text-sm" data-id="element-707">
                <strong data-id="element-708">Output:</strong> A documented content framework that
                becomes your blueprint for all future content creation.
              </div>
            </div>
          </div>

          <div className="flex justify-center mb-16" data-id="element-709">
            <ArrowDown className="w-8 h-8 text-salon-teal" data-id="element-710" />
          </div>

          {/* Stage 3: Machine Building */}
          <div className="mb-16" data-id="element-711">
            <div className="flex items-center gap-4 mb-8" data-id="element-712">
              <div className="w-12 h-12 rounded-full bg-salon-yellow flex items-center justify-center text-white font-bold" data-id="element-713">
                03
              </div>
              <div data-id="element-714">
                <h2 className="text-2xl font-bold" data-id="element-715">
                  Machine: Build Your Content Engine
                </h2>
                <p className="font-mono text-sm text-[#9ca3af]" data-id="element-716">Weeks 4-6</p>
              </div>
            </div>

            <div className="ml-16 bg-white border-2 border-salon-yellow p-8" data-id="element-717">
              <div className="flex items-start gap-6 mb-6" data-id="element-718">
                <Cog className="w-12 h-12 text-salon-yellow flex-shrink-0" data-id="element-719" />
                <div data-id="element-720">
                  <h3 className="text-xl font-bold mb-3" data-id="element-721">
                    Build Your AI-Powered Content Machine
                  </h3>
                  <p className="text-[#1f2933]/80 mb-4" data-id="element-722">
                    Now we operationalize your framework. You'll build systems
                    that generate ideas, create drafts, and streamline
                    production while maintaining your authentic voice.
                  </p>
                  <ul className="space-y-2" data-id="element-723">
                    <li className="flex items-start gap-2" data-id="element-724">
                      <span className="text-salon-yellow font-bold" data-id="element-725">→</span>
                      <span className="text-sm" data-id="element-726">
                        Set up your AI content generation workflow
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-727">
                      <span className="text-salon-yellow font-bold" data-id="element-728">→</span>
                      <span className="text-sm" data-id="element-729">
                        Create your idea generation machine
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-730">
                      <span className="text-salon-yellow font-bold" data-id="element-731">→</span>
                      <span className="text-sm" data-id="element-732">
                        Build templates and prompts for consistency
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-733">
                      <span className="text-salon-yellow font-bold" data-id="element-734">→</span>
                      <span className="text-sm" data-id="element-735">
                        Design your content refinement process
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="bg-salon-yellow/10 border-l-4 border-salon-yellow p-4 font-mono text-sm" data-id="element-736">
                <strong data-id="element-737">Output:</strong> A functioning content machine that can
                generate 10-20 content ideas per week and produce first drafts
                in minutes, not hours.
              </div>
            </div>
          </div>

          <div className="flex justify-center mb-16" data-id="element-738">
            <ArrowDown className="w-8 h-8 text-salon-yellow" data-id="element-739" />
          </div>

          {/* Stage 4: Momentum */}
          <div className="mb-16" data-id="element-740">
            <div className="flex items-center gap-4 mb-8" data-id="element-741">
              <div className="w-12 h-12 rounded-full bg-salon-red flex items-center justify-center text-white font-bold" data-id="element-742">
                04
              </div>
              <div data-id="element-743">
                <h2 className="text-2xl font-bold" data-id="element-744">
                  Momentum: Consistency & Accountability
                </h2>
                <p className="font-mono text-sm text-[#9ca3af]" data-id="element-745">Weeks 7-10</p>
              </div>
            </div>

            <div className="ml-16 grid md:grid-cols-2 gap-6" data-id="element-746">
              <div className="border-2 border-salon-red p-6 bg-white" data-id="element-747">
                <h3 className="font-bold mb-3 text-lg" data-id="element-748">Accountability Pods</h3>
                <p className="text-sm text-[#1f2933]/80 mb-4" data-id="element-749">
                  Join a small group of peers who keep you consistent through
                  structured pressure and shared momentum.
                </p>
                <ul className="space-y-2 text-sm" data-id="element-750">
                  <li data-id="element-751">• Weekly check-ins and progress reviews</li>
                  <li data-id="element-752">• Peer feedback on content</li>
                  <li data-id="element-753">• Shared wins and challenges</li>
                </ul>
              </div>
              <div className="border-2 border-salon-red p-6 bg-white" data-id="element-754">
                <h3 className="font-bold mb-3 text-lg" data-id="element-755">Community Support</h3>
                <p className="text-sm text-[#1f2933]/80 mb-4" data-id="element-756">
                  Access a broader community of professionals building authority
                  together.
                </p>
                <ul className="space-y-2 text-sm" data-id="element-757">
                  <li data-id="element-758">• Monthly community calls</li>
                  <li data-id="element-759">• Resource sharing and collaboration</li>
                  <li data-id="element-760">• Opportunity exchange</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-center mb-16" data-id="element-761">
            <ArrowDown className="w-8 h-8 text-salon-red" data-id="element-762" />
          </div>

          {/* Stage 5: Transformation */}
          <div className="mb-16" data-id="element-763">
            <div className="flex items-center gap-4 mb-8" data-id="element-764">
              <div className="w-12 h-12 rounded-full bg-[#1f2933] flex items-center justify-center text-white font-bold" data-id="element-765">
                05
              </div>
              <div data-id="element-766">
                <h2 className="text-2xl font-bold" data-id="element-767">
                  Transformation: Where You'll Be
                </h2>
                <p className="font-mono text-sm text-[#9ca3af]" data-id="element-768">Ongoing</p>
              </div>
            </div>

            <div className="ml-16 bg-gradient-to-br from-salon-red/10 via-salon-teal/10 to-salon-yellow/10 border-2 border-[#1f2933] p-8" data-id="element-769">
              <div className="flex items-start gap-6 mb-6" data-id="element-770">
                <Rocket className="w-12 h-12 text-[#1f2933] flex-shrink-0" data-id="element-771" />
                <div data-id="element-772">
                  <h3 className="text-xl font-bold mb-3" data-id="element-773">
                    Your Authority Compounds
                  </h3>
                  <p className="text-[#1f2933]/80 mb-6" data-id="element-774">
                    With your framework and machine in place, consistency
                    becomes effortless. Authority compounds. Opportunities
                    multiply.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4" data-id="element-775">
                <div className="bg-white p-4 border border-[#1f2933]" data-id="element-776">
                  <TrendingUp className="w-6 h-6 text-salon-red mb-2" data-id="element-777" />
                  <h4 className="font-bold mb-2" data-id="element-778">Consistent Output</h4>
                  <p className="text-sm text-[#1f2933]/80" data-id="element-779">
                    3-5 pieces of quality content per week, effortlessly
                  </p>
                </div>
                <div className="bg-white p-4 border border-[#1f2933]" data-id="element-780">
                  <TrendingUp className="w-6 h-6 text-salon-teal mb-2" data-id="element-781" />
                  <h4 className="font-bold mb-2" data-id="element-782">Growing Authority</h4>
                  <p className="text-sm text-[#1f2933]/80" data-id="element-783">
                    Recognized as a thought leader in your space
                  </p>
                </div>
                <div className="bg-white p-4 border border-[#1f2933]" data-id="element-784">
                  <TrendingUp className="w-6 h-6 text-salon-yellow mb-2" data-id="element-785" />
                  <h4 className="font-bold mb-2" data-id="element-786">Revenue Streams</h4>
                  <p className="text-sm text-[#1f2933]/80" data-id="element-787">
                    Opportunities, clients, and income from your authority
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white border-t-2 border-dashed border-[#9ca3af]" data-id="element-788">
        <div className="mx-auto max-w-3xl px-4 text-center" data-id="element-789">
          <h2 className="text-3xl font-bold mb-6" data-id="element-790">
            Ready to Build Your Authority Engine?
          </h2>
          <p className="text-lg text-[#1f2933]/80 mb-8" data-id="element-791">
            This blueprint is your roadmap. The journey starts with a single
            decision.
          </p>
          <Button size="large" onClick={() => onNavigate && onNavigate('/initiate-access')} className="bg-salon-red hover:bg-salon-red/90 border-salon-red" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-793" />} data-id="element-792">
            INITIATE_ACCESS
          </Button>
        </div>
      </section>

      <Footer data-id="element-794" />
    </div>;
}